import { api } from "@glasshalf/api-client";
import type { ColumnDef } from "@tanstack/react-table";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ConfirmImpactDialog } from "../../components/ConfirmImpactDialog";
import { DataTable } from "../../components/DataTable";
import { EmptyState } from "../../components/EmptyState";
import { formatNumber, normalizeText } from "../../lib/format";
import { locations, productRows } from "../../lib/selectors";
import type { AppData, ProductRow, SkuRow } from "../../lib/types";

export function ReceivingPage({ data }: { data: AppData }) {
  const queryClient = useQueryClient();
  const products = productRows(data);
  const [query, setQuery] = useState("");
  const [expandedProductKey, setExpandedProductKey] = useState<string | null>(null);
  const locationOptions = locations(data).filter((location) => {
    const type = String(location.locationType || "");
    return location.active !== false && type !== "receiving";
  });
  const [toLocation, setToLocation] = useState(String(locationOptions[0]?.code || "WH_MAIN"));
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [expectedQuantity, setExpectedQuantity] = useState(0);
  const [reason, setReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const selectedProduct = products.find((product) => product.productKey === expandedProductKey) || null;
  const selectedRows = selectedProduct?.rows || [];
  const filtered = products.filter((product) => {
    const text = normalizeText([
      product.productName,
      product.productKey,
      product.seasonCode,
      product.categoryName,
      ...product.rows.flatMap((row) => [row.sku, row.readableAlias, row.barcode, row.colorwayName, row.sizeName])
    ].join(" "));
    return !query || text.includes(normalizeText(query));
  });
  const sizes = useMemo(() => {
    const seen = new Map<string, SkuRow>();
    for (const row of selectedRows) {
      const key = String(row.sizeName || row.sizeCode || "-");
      if (!seen.has(key)) seen.set(key, row);
    }
    return [...seen.keys()];
  }, [selectedRows]);
  const colorGroups = useMemo(() => {
    const groups = new Map<string, SkuRow[]>();
    for (const row of selectedRows) {
      const key = String(row.colorwayName || row.colorwayCode || "-");
      groups.set(key, [...(groups.get(key) || []), row]);
    }
    return [...groups.entries()];
  }, [selectedRows]);
  const lines = selectedRows
    .map((row) => ({ sku: String(row.sku || ""), quantity: Number(quantities[String(row.sku || "")] || 0) }))
    .filter((line) => line.sku && line.quantity > 0);
  const totalQuantity = lines.reduce((sum, line) => sum + line.quantity, 0);
  const varianceQuantity = expectedQuantity ? totalQuantity - expectedQuantity : 0;
  const mutation = useMutation({
    mutationFn: () => api.receive({
      toLocationCode: toLocation,
      note: [reason, expectedQuantity ? `예정 ${expectedQuantity}개 / 실제 ${totalQuantity}개 / 차이 ${varianceQuantity > 0 ? "+" : ""}${varianceQuantity}개` : ""].filter(Boolean).join(" · "),
      lines
    }),
    onSuccess: async () => {
      setConfirmOpen(false);
      setQuantities({});
      setExpectedQuantity(0);
      setReason("");
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    }
  });
  const columns = useMemo<ColumnDef<ProductRow>[]>(() => [
    {
      accessorKey: "productName",
      header: "상품명",
      cell: ({ row }) => (
        <div className="cell-primary">
          <strong>{row.original.productName || "-"}</strong>
        </div>
      ),
      size: 300
    },
    { accessorKey: "productKey", header: "내부 상품코드", cell: ({ row }) => row.original.productKey },
    { accessorKey: "seasonCode", header: "시즌", cell: ({ row }) => row.original.seasonCode },
    { accessorKey: "categoryName", header: "카테고리", cell: ({ row }) => row.original.categoryName },
    { accessorKey: "colorCount", header: "컬러", cell: ({ row }) => <span className="num">{formatNumber(row.original.colorCount)}</span> },
    { accessorKey: "sizeCount", header: "사이즈", cell: ({ row }) => <span className="num">{formatNumber(row.original.sizeCount)}</span> },
    { accessorKey: "skuCount", header: "SKU", cell: ({ row }) => <span className="num">{formatNumber(row.original.skuCount)}</span> },
    { accessorKey: "onHandTotal", header: "실재고", cell: ({ row }) => <span className="num">{formatNumber(row.original.onHandTotal)}</span> }
  ], []);

  if (!products.length) {
    return <EmptyState title="입고할 상품이 없습니다." body="Products에서 상품과 SKU를 먼저 등록해 주세요." />;
  }

  return (
    <div className="page-stack">
      <section className="toolbar-panel">
        <div className="toolbar-row">
          <label className="search-field">
            검색
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="상품명, 상품코드, SKU, 바코드, 컬러, 사이즈"
            />
          </label>
        </div>
      </section>
      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>입고 상품 목록</h2>
            <p>{formatNumber(filtered.length)}개 상품 · 상품을 선택하면 컬러 x 사이즈 입고 수량을 입력합니다.</p>
          </div>
        </div>
        <DataTable
          columns={columns}
          data={filtered}
          getRowId={(row) => row.productKey}
          selectedId={expandedProductKey || ""}
          onSelect={(record) => {
            setExpandedProductKey((current) => current === record.productKey ? null : record.productKey);
            setQuantities({});
            setExpectedQuantity(0);
          }}
          emptyTitle="검색 결과가 없습니다."
          renderExpandedRow={(product) => (
            <ReceivingProductMatrix
              product={product}
              sizes={sizes}
              colorGroups={colorGroups}
              quantities={quantities}
              expectedQuantity={expectedQuantity}
              totalQuantity={totalQuantity}
              varianceQuantity={varianceQuantity}
              locationOptions={locationOptions}
              toLocation={toLocation}
              onLocationChange={setToLocation}
              onExpectedQuantityChange={setExpectedQuantity}
              onQuantityChange={(sku, quantity) => setQuantities((current) => ({ ...current, [sku]: quantity }))}
              onSubmit={() => setConfirmOpen(true)}
            />
          )}
        />
      </section>
      <ConfirmImpactDialog
        open={confirmOpen}
        title="입고 등록 확인"
        currentValue={expectedQuantity ? `예정 ${formatNumber(expectedQuantity)}개 / 선택 SKU ${lines.length}개` : `${locationLabel(locationOptions, toLocation)} 위치에 선택 SKU ${lines.length}개`}
        nextValue={`총 ${formatNumber(totalQuantity)}개 입고 반영${expectedQuantity ? ` · 차이 ${varianceQuantity > 0 ? "+" : ""}${formatNumber(varianceQuantity)}개` : ""}`}
        externalImpact="온라인몰 수량은 변경하지 않습니다."
        auditTrail="입고 위치, SKU, 수량, 사유가 재고 원장과 감사 로그에 남습니다."
        reason={reason}
        confirmLabel="입고 등록"
        busy={mutation.isPending}
        onReasonChange={setReason}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => mutation.mutate()}
      />
    </div>
  );
}

function ReceivingProductMatrix({
  product,
  sizes,
  colorGroups,
  quantities,
  expectedQuantity,
  totalQuantity,
  varianceQuantity,
  locationOptions,
  toLocation,
  onLocationChange,
  onExpectedQuantityChange,
  onQuantityChange,
  onSubmit
}: {
  product: ProductRow;
  sizes: string[];
  colorGroups: Array<[string, SkuRow[]]>;
  quantities: Record<string, number>;
  expectedQuantity: number;
  totalQuantity: number;
  varianceQuantity: number;
  locationOptions: AppData["inventory"]["locations"];
  toLocation: string;
  onLocationChange: (value: string) => void;
  onExpectedQuantityChange: (value: number) => void;
  onQuantityChange: (sku: string, quantity: number) => void;
  onSubmit: () => void;
}) {
  return (
    <div className="products-sku-preview receiving-product-workflow">
      <div className="products-sku-preview-head">
        <div>
          <h3>{product.productName}</h3>
          <span>{product.seasonCode} · {formatNumber(product.skuCount)} SKU · 현재 실재고 {formatNumber(product.onHandTotal)}</span>
        </div>
      </div>
      <div className="table-frame">
        <table className="matrix-table">
          <thead>
            <tr>
              <th>컬러</th>
              {sizes.map((size) => <th key={size}>{formatSizeLabel(size)}</th>)}
              <th>합계</th>
            </tr>
          </thead>
          <tbody>
            {colorGroups.map(([color, rows]) => {
              const rowTotal = rows.reduce((sum, row) => sum + Number(quantities[String(row.sku || "")] || 0), 0);
              return (
                <tr key={color}>
                  <th>
                    <strong>{color}</strong>
                    <span>{formatNumber(rows.reduce((sum, row) => sum + Number(row.onHandTotal || 0), 0))} 총재고</span>
                  </th>
                  {sizes.map((size) => {
                    const skuRow = rows.find((row) => String(row.sizeName || row.sizeCode || "-") === size);
                    const sku = String(skuRow?.sku || "");
                    return (
                      <td key={`${color}:${size}`}>
                        {skuRow ? (
                          <input
                            type="number"
                            min={0}
                            value={quantities[sku] || 0}
                            aria-label={`${color} ${formatSizeLabel(size)} 입고 수량`}
                            onChange={(event) => onQuantityChange(sku, Math.max(0, Number(event.target.value || 0)))}
                          />
                        ) : <span className="muted">-</span>}
                      </td>
                    );
                  })}
                  <td className="num"><span className="matrix-total-value">{formatNumber(rowTotal)}</span></td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <th>총 입고</th>
              {sizes.map((size) => (
                <td key={size} className="num">
                  <span className="matrix-total-value">
                    {formatNumber(product.rows
                      .filter((row) => String(row.sizeName || row.sizeCode || "-") === size)
                      .reduce((sum, row) => sum + Number(quantities[String(row.sku || "")] || 0), 0))}
                  </span>
                </td>
              ))}
              <td className="num"><span className="matrix-total-value">{formatNumber(totalQuantity)}</span></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="receiving-actions">
        <label>
          입고 예정
          <input
            type="number"
            min={0}
            value={expectedQuantity || 0}
            onChange={(event) => onExpectedQuantityChange(Math.max(0, Number(event.target.value || 0)))}
          />
        </label>
        <label>
          입고 위치
          <select value={toLocation} onChange={(event) => onLocationChange(event.target.value)}>
            {locationOptions.map((location) => <option key={String(location.code)} value={String(location.code)}>{String(location.name || location.code)}</option>)}
          </select>
        </label>
        <div className="receiving-submit">
          <span>{expectedQuantity ? `예정 대비 차이 ${varianceQuantity > 0 ? "+" : ""}${formatNumber(varianceQuantity)}개 · ` : ""}온라인몰 할당은 변경하지 않고 선택 위치의 실제 재고만 반영합니다.</span>
          <button type="button" className="primary" disabled={!totalQuantity} onClick={onSubmit}>입고 등록</button>
        </div>
      </div>
    </div>
  );
}

function locationLabel(options: AppData["inventory"]["locations"], code: string): string {
  const match = options.find((location) => String(location.code) === code);
  return String(match?.name || code);
}

function formatSizeLabel(size: string): string {
  return String(size || "-").toUpperCase();
}
